/**
 * O que faz: Centraliza operações administrativas do painel, incluindo validação de admin,
 * busca de clínicas e leitura das estatísticas globais via RPC do Supabase.
 * Onde usa: Painel Administrativo e telas internas de gestão de clínicas.
 * Conexões: supabase client, auth.getUser, tabela user_profiles, RPC get_admin_clinicas_stats.
 * Tabelas: user_profiles, clinicas, leads, chat_mensagens, whatsapp_status.
 * Autor: IA
 * Data: 08/04/2026
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UseSupabaseAdminOptions {
  autoLoadClinicas?: boolean;
}

/**
 * Hook para operações administrativas no Supabase
 * 
 * Centraliza todas as operações que requerem privilégios administrativos,
 * incluindo busca de clínicas, usuários, estatísticas e configurações.
 */
export const useSupabaseAdmin = (options: UseSupabaseAdminOptions = {}) => {
  // Permite desligar a carga automática de clínicas em telas que só precisam da RPC agregada.
  // Motivo: o painel administrativo principal não usa a lista bruta e pode evitar uma consulta extra.
  const { autoLoadClinicas = true } = options;
  const [loading, setLoading] = useState(false);
  const [clinicas, setClinicas] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);

  // Traduz o erro bruto do Supabase para uma mensagem útil na interface.
  // Motivo: antes o hook escondia a falha e a tela parecia vazia, como se não existissem dados.
  const criarErroAdministrativo = (contexto: string, error: unknown): Error => {
    const erroSupabase = error as {
      code?: string;
      details?: string;
      hint?: string;
      message?: string;
    };

    // Detecta o bloqueio explícito da RPC para usuários que o banco não reconheceu como admin.
    // Regra de negócio: a permissão final precisa bater no Postgres, não só no estado do React.
    if (erroSupabase?.code === '42501' || erroSupabase?.message?.includes('access_denied')) {
      return new Error(
        'O banco negou acesso à consulta administrativa. Verifique se a função is_current_user_admin() reconhece este usuário como admin.'
      );
    }

    // Detecta quando a RPC depende de tabela ou coluna que não existe no banco atual.
    // Consequência: o administrador passa a ver que o problema é estrutural, não um painel sem dados.
    if (
      erroSupabase?.message?.includes('whatsapp_status') ||
      erroSupabase?.message?.includes('column') ||
      erroSupabase?.message?.includes('relation')
    ) {
      return new Error(
        `A RPC administrativa encontrou uma divergência de estrutura no banco ao ${contexto}. Mensagem original: ${erroSupabase.message}`
      );
    }

    // Mantém a mensagem original quando não há um padrão conhecido.
    // Motivo: mesmo um erro genérico é mais útil do que zerar a tela em silêncio.
    return new Error(
      erroSupabase?.message || `Erro inesperado ao ${contexto}.`
    );
  };

  // Registra no console todos os detalhes técnicos retornados pelo Supabase.
  // Motivo: facilita comparar code/message/details/hint sem depender de logs resumidos.
  const registrarErroAdministrativo = (contexto: string, error: unknown) => {
    const erroSupabase = error as {
      code?: string;
      details?: string;
      hint?: string;
      message?: string;
    };

    console.error(`[useSupabaseAdmin] Falha ao ${contexto}:`, {
      code: erroSupabase?.code ?? null,
      message: erroSupabase?.message ?? null,
      details: erroSupabase?.details ?? null,
      hint: erroSupabase?.hint ?? null,
      rawError: error,
    });
  };

  // ============================================================================
  // Verificar permissão de admin internamente via user_profiles
  // ============================================================================
  // IMPORTANTE: O sistema usa user_profiles.profile_type para controlar acesso admin
  // A promoção a admin deve ser feita manualmente no banco de dados (não via frontend)
  const verificarPermissaoAdmin = async (): Promise<boolean> => {
    try {
      const userId = await obterUserIdAtual();

      // Busca o profile_type na tabela user_profiles
      const { data, error } = await supabase
        .from('user_profiles')
        .select('profile_type')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.warn('⚠️ Erro ao verificar permissão admin:', error.message);
        setIsAdmin(false);
        return false;
      }

      const isAdminUser = data?.profile_type === 'admin';

      if (!isAdminUser) {
        console.warn('⚠️ Acesso admin negado - profile_type:', data?.profile_type);
      }

      // Atualiza o estado interno do hook
      setIsAdmin(isAdminUser);
      return isAdminUser;
    } catch (error) {
      console.error('Erro ao verificar permissão admin:', error);
      setIsAdmin(false);
      return false;
    }
  };

  // Buscar todas as clínicas (apenas para admin)
  const buscarTodasClinicas = async () => {
    if (!isAdmin) {
      return [];
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('clinicas')
        .select('*')
        .order('nome');

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Buscar clínica específica por ID
  const buscarClinicaPorId = async (clinicaId: string) => {
    if (!isAdmin) {
      throw new Error('Acesso negado');
    }

    try {
      const { data, error } = await supabase
        .from('clinicas')
        .select('*')
        .eq('id', clinicaId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Buscar estatísticas de leads de uma clínica
  const buscarEstatisticasDeLeadsDaClinica = async (clinicaId: string, startDate?: Date, endDate?: Date) => {
    if (!isAdmin) {
      throw new Error('Acesso negado');
    }

    try {
      let query = supabase
        .from('leads')
        .select('*')
        .eq('clinica_id', clinicaId);

      if (startDate && endDate) {
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Calcular estatísticas
      const totalLeads = data?.length || 0;
      const leadsConvertidos = data?.filter(lead => lead.convertido).length || 0;
      const taxaConversao = totalLeads > 0 ? (leadsConvertidos / totalLeads) * 100 : 0;
      const leadsAnuncios = data?.filter(lead => lead.origem_lead === 'anuncio').length || 0;

      const estatisticas = {
        totalLeads,
        leadsConvertidos,
        taxaConversao: Math.round(taxaConversao * 100) / 100,
        leadsAnuncios
      };

      return estatisticas;
    } catch (error) {
      throw error;
    }
  };

  // Buscar estatísticas de todas as clínicas via RPC agregada.
  // Uma única chamada substitui o loop N+1 anterior (17 clínicas × 7 queries = 119 requests).
  // skipAdminCheck: pular verificação quando já confirmado que é admin (evita race condition)
  const buscarEstatisticasClinicas = async (startDate?: Date, endDate?: Date, skipAdminCheck?: boolean) => {
    if (!isAdmin && !skipAdminCheck) {
      console.log('⚠️ buscarEstatisticasClinicas: usuário não é admin, retornando array vazio');
      return [];
    }

    try {
      const { data, error } = await supabase.rpc('get_admin_clinicas_stats', {
        p_start_date: startDate?.toISOString() ?? null,
        p_end_date:   endDate?.toISOString()   ?? null,
      } as any);

      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        ...row,
        total_leads:          Number(row.total_leads),
        leads_convertidos:    Number(row.leads_convertidos),
        leads_anuncios_count: Number(row.leads_anuncios_count),
        leads_links_count:    Number(row.leads_links_count),
        mensagens_enviadas:   Number(row.mensagens_enviadas),
        mensagens_recebidas:  Number(row.mensagens_recebidas),
        total_usuarios:       Number(row.total_usuarios),
      }));
    } catch (error) {
      // Propaga a falha para a tela mostrar o motivo real do problema.
      // Motivo: retornar array vazio mascarava erro de RPC como "zero clínicas cadastradas".
      registrarErroAdministrativo('buscar estatísticas das clínicas', error);
      throw criarErroAdministrativo('buscar estatísticas das clínicas', error);
    }
  };

  // Buscar KPIs globais via RPC agregada (uma única chamada ao banco).
  // A RPC retorna tanto métricas do período quanto totais all-time na mesma resposta,
  // eliminando a segunda chamada sem filtro que existia antes.
  // skipAdminCheck: pular verificação quando já confirmado que é admin (evita race condition)
  const buscarKPIsGlobais = async (startDate?: Date, endDate?: Date, skipAdminCheck?: boolean) => {
    if (!isAdmin && !skipAdminCheck) {
      console.log('⚠️ buscarKPIsGlobais: usuário não é admin, retornando null');
      return null;
    }

    try {
      const { data, error } = await supabase.rpc('get_admin_clinicas_stats', {
        p_start_date: startDate?.toISOString() ?? null,
        p_end_date:   endDate?.toISOString()   ?? null,
      } as any);

      if (error) throw error;

      const clinicas = (data ?? []) as any[];
      const clinicasAtivas = clinicas.filter((c: any) => c.status === 'ativo');

      // Métricas do período (filtradas por p_start_date/p_end_date)
      const novosLeadsPeriodo    = clinicasAtivas.reduce((s: number, c: any) => s + Number(c.total_leads), 0);
      const leadsAnunciosPeriodo = clinicasAtivas.reduce((s: number, c: any) => s + Number(c.leads_anuncios_count), 0);
      const leadsLinksPeriodo    = clinicasAtivas.reduce((s: number, c: any) => s + Number(c.leads_links_count), 0);

      // Totais all-time (a RPC retorna total_leads_all mesmo com filtro de data)
      const totalLeads       = clinicasAtivas.reduce((s: number, c: any) => s + Number(c.total_leads_all), 0);
      const leadsConvertidos = clinicasAtivas.reduce((s: number, c: any) => s + Number(c.leads_convertidos_all), 0);
      const taxaConversao    = totalLeads > 0 ? (leadsConvertidos / totalLeads) * 100 : 0;

      // Usuários: soma de todas as clínicas (não só ativas)
      const totalUsuarios = clinicas.reduce((s: number, c: any) => s + Number(c.total_usuarios), 0);

      return {
        totalLeads,
        leadsConvertidos,
        novosLeadsPeriodo,
        leadsAnunciosPeriodo,
        leadsLinksPeriodo,
        taxaConversao: Math.round(taxaConversao * 100) / 100,
        totalClinicas: clinicasAtivas.length,
        totalUsuarios,
      };
    } catch (error) {
      // Propaga a falha para a UI não tratar erro de banco como KPI zerado.
      // Consequência: o administrador recebe diagnóstico explícito em vez de números vazios.
      registrarErroAdministrativo('buscar KPIs globais', error);
      throw criarErroAdministrativo('buscar KPIs globais', error);
    }
  };

  // Obter ID do usuário atual
  const obterUserIdAtual = async (): Promise<string> => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error) {
        throw error;
      }

      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      return user.id;
    } catch (error) {
      throw error;
    }
  };

  // Atualizar configurações da Evolution API
  const atualizarConfiguracaoEvolution = async (
    clinicaId: string,
    instanceName?: string,
    apiKey?: string
  ): Promise<any> => {
    if (!isAdmin) {
      throw new Error('Acesso negado: usuário não é administrador');
    }

    try {
      console.log(`🔧 [useSupabaseAdmin] Atualizando configuração Evolution da clínica: ${clinicaId}`);

      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (instanceName !== undefined) {
        updateData.evolution_instance_name = instanceName;
        console.log(`📝 [useSupabaseAdmin] Instance name: ${instanceName}`);

        const trimmed = instanceName.trim();
        if (trimmed) {
          const { data: duplicate, error: duplicateError } = await supabase
            .from('clinicas')
            .select('id, nome')
            .eq('evolution_instance_name', trimmed)
            .neq('id', clinicaId)
            .maybeSingle();

          if (duplicateError) {
            throw duplicateError;
          }

          if (duplicate) {
            throw new Error(
              `Esta instância WhatsApp já está vinculada à clínica "${duplicate.nome}". Cada clínica precisa de uma instância exclusiva.`
            );
          }
        }
      }

      if (apiKey !== undefined) {
        updateData.evolution_api_key = apiKey;
        console.log(`🔑 [useSupabaseAdmin] API key: ${apiKey ? '[FORNECIDA]' : '[VAZIA]'}`);
      }

      if (Object.keys(updateData).length <= 1) {
        console.warn('⚠️ [useSupabaseAdmin] Nenhum dado para atualizar além do timestamp');
        return null;
      }

      const { data, error } = await supabase
        .from('clinicas')
        .update(updateData)
        .eq('id', clinicaId)
        .select();

      if (error) {
        console.error('❌ [useSupabaseAdmin] Erro ao atualizar configuração Evolution:', error);
        throw error;
      }

      console.log('✅ [useSupabaseAdmin] Configuração Evolution atualizada com sucesso:', data);

      // Atualizar estado local das clínicas se existir
      setClinicas(prevClinicas =>
        prevClinicas.map(clinica =>
          clinica.id === clinicaId
            ? { ...clinica, ...updateData }
            : clinica
        )
      );

      return data?.[0] || null;
    } catch (error) {
      console.error('❌ [useSupabaseAdmin] Erro ao atualizar configuração Evolution:', error);
      throw error;
    }
  };

  // Atualizar configurações de webhook da clínica
  const atualizarConfiguracaoWebhook = async (
    clinicaId: string,
    webhookType?: string,
    webhookUrl?: string
  ) => {
    try {
      console.log(`🔧 [useSupabaseAdmin] Atualizando configuração de webhook da clínica: ${clinicaId}`);
      console.log('- Tipo:', webhookType);
      console.log('- URL:', webhookUrl);

      // Preparar dados para atualização
      const updateData: any = {};

      if (webhookType !== undefined) {
        updateData.webhook_type = webhookType;
      }

      if (webhookType === 'personalizado' && webhookUrl !== undefined) {
        updateData.webhook_url = webhookUrl;
      } else if (webhookType === 'padrao') {
        // Limpar URL quando volta para padrão
        updateData.webhook_url = null;
      }

      const { data, error } = await supabase
        .from('clinicas')
        .update(updateData)
        .eq('id', clinicaId)
        .select()
        .single();

      if (error) {
        console.error('❌ [useSupabaseAdmin] Erro ao atualizar configuração de webhook:', error);
        throw error;
      }

      console.log('✅ [useSupabaseAdmin] Configuração de webhook atualizada com sucesso');
      return data;
    } catch (error) {
      console.error('❌ [useSupabaseAdmin] Erro na função atualizarConfiguracaoWebhook:', error);
      throw error;
    }
  };

  // Verificar status de admin na inicialização
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setAdminCheckLoading(true);

        const adminStatus = await verificarPermissaoAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        setIsAdmin(false);
      } finally {
        setAdminCheckLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  const recarregarClinicas = async () => {
    if (!isAdmin) {
      return [];
    }

    const clinicasData = await buscarTodasClinicas();
    setClinicas(clinicasData);
    return clinicasData;
  };

  // Carregar clínicas inicialmente quando usuário for admin
  useEffect(() => {
    const carregarClinicas = async () => {
      // Respeita a configuração da tela para não disparar SELECT em cenários onde a lista não será exibida.
      // Consequência: reduz latência e carga no banco sem quebrar componentes que ainda dependem desse comportamento.
      if (autoLoadClinicas && isAdmin && !adminCheckLoading) {
        try {
          await recarregarClinicas();
        } catch (error) {
          setClinicas([]);
        }
      }
    };

    carregarClinicas();
  }, [autoLoadClinicas, isAdmin, adminCheckLoading]);

  return {
    loading: loading || adminCheckLoading,
    clinicas,
    isAdmin,
    adminCheckLoading,
    obterUserIdAtual,
    // configurarComoAdmin foi REMOVIDO por segurança
    verificarPermissaoAdmin,
    buscarTodasClinicas,
    buscarClinicaPorId,
    buscarEstatisticasDeLeadsDaClinica,
    buscarEstatisticasClinicas,
    buscarKPIsGlobais,
    atualizarConfiguracaoEvolution,
    atualizarConfiguracaoWebhook,
    recarregarClinicas,
  };
};
